import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import api from '../services/api'
import type { AppearancePresetId } from '@/utils/appearanceTheme'
import {
  MODULO_POR_DEFECTO,
  normalizarKilosPorBulto,
  normalizarModulos,
  numeroPositivoONull,
  resolverModuloPorDefecto,
  type ModuloId,
} from '@/utils/modulos'
import { normalizarModoDivision, type ModoDivision } from '@/utils/division-galpon'

interface AuthUser {
  id: number
  email: string
  username: string
  role: string
  clienteId?: number
  clienteNombre?: string
  clienteAppearancePreset?: AppearancePresetId | null
  // Link al tablero de Power BI del cliente (lo define el superadmin). Puede no existir.
  clientePowerBiUrl?: string | null
  /** Kilos por bulto de alimento con los que trabaja el cliente. */
  clienteKilosPorBulto: number
  /** Cómo divide sus galpones: sin dividir, secciones fijas o divisiones móviles. */
  clienteModoDivision: ModoDivision
  /** Peso al que el cliente normaliza la conversión para comparar lotes. */
  clientePesoReferenciaConversion?: number | null
  /** Conversión que se descuenta por kg por encima de ese peso. */
  clienteAjusteConversionPorKg?: number | null
  // Módulos de negocio habilitados para el cliente y cuál abre por defecto.
  clienteModulos?: ModuloId[]
  clienteModuloPorDefecto?: ModuloId
}

/** Identidad tomada de la respuesta de login; si no llega, se usa la de /users/me. */
interface IdentidadLogin {
  id: number
  email: string
  username: string
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const initialized = ref(false)

  const isAuthenticated = computed(() => !!user.value)
  const roleName = computed(() => user.value?.role ?? null)

  const isSuperadmin = computed(() => user.value?.role === 'superadmin')

  // Los módulos VIGENTES en pantalla no se resuelven aquí: el superadmin no tiene
  // cliente propio y opera sobre el que elige en el selector. Esa resolución vive
  // en `useModulos()`, que es la única fuente de verdad para menú y rutas.

  /** Arma el AuthUser a partir del payload de /api/users/me?populate=*. */
  function construirUsuario(userData: any, identidad?: IdentidadLogin): AuthUser {
    const clienteData = userData?.cliente
    const clienteModulos = normalizarModulos(clienteData?.modulos)

    return {
      id: identidad?.id ?? userData.id,
      email: identidad?.email ?? userData.email,
      username: identidad?.username ?? userData.username,
      role: userData.role.type,
      clienteId: clienteData?.id,
      clienteNombre:
        clienteData?.NombreCliente ?? clienteData?.nombre ?? clienteData?.name,
      clienteAppearancePreset: clienteData?.appearancePreset ?? null,
      clientePowerBiUrl: clienteData?.PowerBiUrl ?? null,
      clienteKilosPorBulto: normalizarKilosPorBulto(clienteData?.KilosPorBultoAlimento),
      clienteModoDivision: normalizarModoDivision(clienteData?.ModoDivisionGalpon),
      clientePesoReferenciaConversion: numeroPositivoONull(clienteData?.PesoReferenciaConversionKg),
      clienteAjusteConversionPorKg: numeroPositivoONull(clienteData?.AjusteConversionPorKg),
      clienteModulos,
      clienteModuloPorDefecto: resolverModuloPorDefecto(
        clienteModulos,
        clienteData?.moduloPorDefecto ?? MODULO_POR_DEFECTO,
      ),
    }
  }

  async function login(email: string, password: string) {
    const res = await api.post('/api/auth/local', {
      identifier: email,
      password,
    })

    const { user: apiUser } = res.data
    localStorage.removeItem('auth_jwt')

    const responseUserData = await api.get('/api/users/me?populate=*')

    user.value = construirUsuario(responseUserData.data, {
      id: apiUser.id,
      email: apiUser.email,
      username: apiUser.username,
    })

    initialized.value = true
    localStorage.setItem('auth_user', JSON.stringify(user.value))
  }

  async function logout() {
    user.value = null
    initialized.value = true
    localStorage.removeItem('auth_user')
    localStorage.removeItem('auth_jwt')

    void api.post('/api/auth/logout', {}).catch(() => {
      // El cierre local ya se aplicó; ignorar error remoto.
    })
  }

  async function checkAuth() {
    try {
      const responseUserData = await api.get('/api/users/me?populate=*')

      user.value = construirUsuario(responseUserData.data)

      localStorage.setItem('auth_user', JSON.stringify(user.value))
    } catch (e) {
      // Solo se cierra la sesión si el servidor dice que ya no vale. Antes
      // CUALQUIER error la borraba, así que un microcorte de red —o el backend
      // reiniciándose— echaba al usuario y le hacía perder lo que tuviera a
      // medias en un formulario. En una granja con mala cobertura eso pasa a
      // diario; el fallo de red no significa que la sesión haya caducado.
      const estado = (e as { response?: { status?: number } })?.response?.status
      const sesionRechazada = estado === 401 || estado === 403
      if (sesionRechazada || !user.value) {
        user.value = null
        localStorage.removeItem('auth_user')
        localStorage.removeItem('auth_jwt')
      }
    } finally {
      initialized.value = true
    }
  }

  function init() {
    const savedUser = localStorage.getItem('auth_user')
    if (savedUser) {
      user.value = JSON.parse(savedUser)
      void checkAuth()
      return
    }

    void checkAuth()
  }

  init()

  return {
    user,
    initialized,
    isAuthenticated,
    roleName,
    isSuperadmin,
    login,
    logout,
    checkAuth,
  }
})
